import { useEffect, useState } from 'react';
import { Input } from '~/components/form/TextField';
import { Dialog } from '~/components/ui/Dialog';
import { PopupDialogNew } from '~/components/ui/PopupDialogNew';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { Button } from '../ui/buttons/Button';

type Props = {
  isOpen: boolean;
  initialName?: string;
  existingCategories: string[];
  onClose: () => void;
  onSubmit: (categoryName: string) => void;
};

export function CategoryCreateDialog({
  isOpen,
  initialName = '',
  existingCategories,
  onClose,
  onSubmit,
}: Props) {
  const [categoryName, setCategoryName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);

  const { isMobile } = useBreakpoint();

  // Reset the input when the dialog opens with a new initial value
  useEffect(() => {
    if (isOpen) {
      setCategoryName(initialName);
      setError(null);
    }
  }, [isOpen, initialName]);

  const handleCreate = () => {
    const trimmedName = categoryName.trim();

    if (!trimmedName) {
      setError('Category name is required');
      return;
    }

    if (existingCategories.includes(trimmedName)) {
      setError('A category with this name already exists');
      return;
    }

    onSubmit(trimmedName);
    handleClose();
  };

  const handleClose = () => {
    setCategoryName('');
    setError(null);
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCategoryName(e.target.value);
    if (error) setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && categoryName.trim()) {
      handleCreate();
    }
  };

  const formContent = (
    <div className='p-5'>
      <div className='mb-4'>
        <Input
          label='Category Name'
          value={categoryName}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder='Enter category name...'
          error={error || undefined}
          required
          autoFocus
        />
      </div>

      <div className='flex justify-end gap-3'>
        <Button variant='secondary' onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleCreate} disabled={!categoryName.trim()}>
          Create
        </Button>
      </div>
    </div>
  );

  // Use PopupDialogNew (BottomSheet) on mobile for proper touch interaction
  // Use Dialog on desktop for the standard modal experience
  if (isMobile) {
    return (
      <PopupDialogNew
        isOpen={isOpen}
        title='New Category'
        onClose={handleClose}
      >
        {formContent}
      </PopupDialogNew>
    );
  }

  return (
    <Dialog show={isOpen} title='New Category' onClose={handleClose}>
      {formContent}
    </Dialog>
  );
}
